import { formatRegionLocation } from '../../utils/regionLocation';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  ErrorOutline as ErrorOutlineIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  Refresh as RefreshIcon,
  ReportProblemOutlined as ReportProblemOutlinedIcon,
} from '@mui/icons-material';
import { OperationalError, OperationalErrorSource } from '../../types/operationalErrors';
import { SOURCE_LABELS, countBySource, relativeTimeLabel } from '../../utils/operationalErrors';
import { useTimezone } from '../../contexts/TimezoneContext';
import { formatUiDateTime } from '../../utils/dateUtils';

const ALL_SOURCES: OperationalErrorSource[] = ['bot', 'transaction', 'execution'];

export interface OperationalErrorsPanelProps {
  errors: OperationalError[];
  loading: boolean;
  /** Set when every source failed to load; rendered instead of the list. */
  error?: string | null;
  /** Sources that failed on the last refresh while others succeeded. */
  degradedSources?: string[];
  onRefresh: () => void;
  lastUpdatedMs?: number | null;
  /** `card`: collapsible header (Executions screen). `embedded`: always open (Operations rail). */
  variant?: 'card' | 'embedded';
  isCollapsed?: boolean;
  onToggle?: () => void;
  title?: string;
}

const sourceChipLabel = (
  source: OperationalErrorSource,
  counts: Record<OperationalErrorSource, number>
) => `${SOURCE_LABELS[source]} (${counts[source]})`;

const IdentityLine: React.FC<{ error: OperationalError }> = ({ error }) => {
  const parts: string[] = [];
  if (error.regionName) parts.push(`Region ${formatRegionLocation(error.regionName)}`);
  if (error.botId) parts.push(`Bot ${error.botId}`);
  if (error.transactionId) parts.push(`Txn ${error.transactionId}`);
  if (error.executionId) parts.push(`Execution ${error.executionId}`);
  if (typeof error.affectedBots === 'number') parts.push(`${error.affectedBots} bots affected`);
  if (typeof error.retryCount === 'number' && error.retryCount > 0) {
    parts.push(`${error.retryCount} retries`);
  }
  if (error.status) parts.push(`Status ${error.status}`);
  if (parts.length === 0) return null;
  return (
    <Typography
      variant="caption"
      sx={{ fontFamily: '"Space Mono", monospace', color: 'text.secondary' }}
    >
      {parts.join(' • ')}
    </Typography>
  );
};

/**
 * Persistent, tenant-scoped feed of operational failures (bot actions, multi-bot transactions and
 * failed executions) merged newest-first. Unlike the action-failure modal and the notification
 * toasts, this panel keeps errors on screen until the backend stops reporting them, so an operator
 * can see why a scale-up half-succeeded minutes after the toast disappeared.
 */
const OperationalErrorsPanel: React.FC<OperationalErrorsPanelProps> = ({
  errors,
  loading,
  error = null,
  degradedSources = [],
  onRefresh,
  lastUpdatedMs = null,
  variant = 'embedded',
  isCollapsed = false,
  onToggle,
  title = 'Operational Errors',
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { timezone } = useTimezone();
  const [sourceFilter, setSourceFilter] = useState<OperationalErrorSource | 'all'>('all');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // Re-render on refresh so "3m ago" does not go stale between polls.
  const nowMs = useMemo(() => Date.now(), [lastUpdatedMs, errors]); // eslint-disable-line react-hooks/exhaustive-deps

  const counts = useMemo(() => countBySource(errors), [errors]);
  const visible = useMemo(
    () => (sourceFilter === 'all' ? errors : errors.filter((e) => e.source === sourceFilter)),
    [errors, sourceFilter]
  );

  // Drop expansions for rows that a refresh removed.
  useEffect(() => {
    const ids = new Set(errors.map((e) => e.id));
    setExpanded((prev) => {
      const next = new Set(Array.from(prev).filter((id) => ids.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [errors]);

  const toggleExpand = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const isEmbedded = variant === 'embedded';
  const collapsed = isEmbedded ? false : isCollapsed;
  const errorCount = errors.filter((e) => e.severity === 'error').length;
  const accent = errors.length > 0 ? theme.palette.error.main : theme.palette.primary.main;

  const body = (
    <CardContent sx={{ pt: 0 }}>
      {degradedSources.length > 0 && !error && (
        <Alert severity="warning" sx={{ mb: 2 }} data-testid="operational-errors-degraded">
          Could not load {degradedSources.join(', ')} errors — showing the rest.
        </Alert>
      )}

      <ToggleButtonGroup
        size="small"
        exclusive
        value={sourceFilter}
        onChange={(_e, value) => setSourceFilter(value ?? 'all')}
        aria-label="Filter errors by source"
        sx={{ mb: 2, flexWrap: 'wrap' }}
      >
        <ToggleButton value="all" aria-label="All sources">
          All ({errors.length})
        </ToggleButton>
        {ALL_SOURCES.map((source) => (
          <ToggleButton key={source} value={source} aria-label={SOURCE_LABELS[source]}>
            {sourceChipLabel(source, counts)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {error ? (
        <Alert severity="error" data-testid="operational-errors-failed">
          {error}
        </Alert>
      ) : loading && errors.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : visible.length === 0 ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ py: 3, fontFamily: '"Space Mono", monospace' }}
          data-testid="operational-errors-empty"
        >
          {errors.length === 0
            ? 'No operational errors reported.'
            : `No ${SOURCE_LABELS[sourceFilter as OperationalErrorSource].toLowerCase()} errors reported.`}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {visible.map((item) => {
            const isOpen = expanded.has(item.id);
            const severityColor =
              item.severity === 'error' ? theme.palette.error.main : theme.palette.warning.main;
            return (
              <Box
                key={item.id}
                data-testid="operational-error-row"
                sx={{
                  border: `1px solid ${alpha(severityColor, isDark ? 0.35 : 0.3)}`,
                  borderLeft: `3px solid ${severityColor}`,
                  borderRadius: 1,
                  backgroundColor: alpha(severityColor, isDark ? 0.08 : 0.05),
                  px: 1.5,
                  py: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <IconButton
                    size="small"
                    onClick={() => toggleExpand(item.id)}
                    aria-label={isOpen ? `Hide details for ${item.message}` : `Show details for ${item.message}`}
                    aria-expanded={isOpen}
                  >
                    {isOpen ? (
                      <KeyboardArrowDownIcon fontSize="small" />
                    ) : (
                      <KeyboardArrowRightIcon fontSize="small" />
                    )}
                  </IconButton>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}
                    >
                      <Chip
                        size="small"
                        label={SOURCE_LABELS[item.source]}
                        sx={{ fontFamily: '"Space Mono", monospace' }}
                      />
                      <Chip
                        size="small"
                        color={item.severity === 'error' ? 'error' : 'warning'}
                        variant="outlined"
                        label={item.action}
                        sx={{ fontFamily: '"Space Mono", monospace' }}
                      />
                      <Box sx={{ flexGrow: 1 }} />
                      <Tooltip title={formatUiDateTime(item.occurredAt, timezone)}>
                        <Typography variant="caption" color="text.secondary">
                          {relativeTimeLabel(item.occurredAt, nowMs)}
                        </Typography>
                      </Tooltip>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: severityColor,
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: isOpen ? 'pre-wrap' : 'nowrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {item.message}
                    </Typography>
                    <IdentityLine error={item} />
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                      {item.details && Object.keys(item.details).length > 0 ? (
                        <Box
                          component="pre"
                          data-testid="operational-error-details"
                          sx={{
                            m: 0,
                            mt: 1,
                            p: 1,
                            fontSize: 12,
                            backgroundColor: 'background.paper',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            overflowX: 'auto',
                          }}
                        >
                          {JSON.stringify(item.details, null, 2)}
                        </Box>
                      ) : (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          data-testid="operational-error-details"
                          sx={{ display: 'block', mt: 1 }}
                        >
                          No additional details recorded.
                        </Typography>
                      )}
                    </Collapse>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {lastUpdatedMs !== null && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 1.5, fontFamily: '"Space Mono", monospace' }}
        >
          Updated {formatUiDateTime(new Date(lastUpdatedMs).toISOString(), timezone)}
        </Typography>
      )}
    </CardContent>
  );

  return (
    <Card
      data-testid="operational-errors-panel"
      sx={{
        backgroundColor: 'background.paper',
        backdropFilter: 'blur(12px)',
        border: `1px solid ${alpha(accent, isDark ? 0.25 : 0.3)}`,
        transition: 'all 0.3s ease',
      }}
    >
      <CardHeader
        avatar={
          <Box
            sx={{
              backgroundColor: alpha(accent, isDark ? 0.2 : 0.25),
              borderRadius: '12px',
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `2px solid ${alpha(accent, isDark ? 0.4 : 0.35)}`,
            }}
          >
            {errorCount > 0 ? (
              <ErrorOutlineIcon sx={{ color: accent }} />
            ) : (
              <ReportProblemOutlinedIcon sx={{ color: accent }} />
            )}
          </Box>
        }
        action={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title="Refresh errors">
              <span>
                <IconButton onClick={onRefresh} disabled={loading} aria-label="Refresh errors">
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
            {!isEmbedded && onToggle && (
              <IconButton onClick={onToggle} aria-label={isCollapsed ? 'Expand errors' : 'Collapse errors'}>
                {isCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
              </IconButton>
            )}
          </Box>
        }
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              sx={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 600, color: 'text.primary' }}
            >
              {title}
            </Typography>
            {errors.length > 0 && (
              <Chip
                size="small"
                color={errorCount > 0 ? 'error' : 'warning'}
                label={errors.length}
                data-testid="operational-errors-count"
              />
            )}
          </Box>
        }
      />
      <Collapse in={!collapsed} timeout="auto">
        {body}
      </Collapse>
    </Card>
  );
};

export default OperationalErrorsPanel;
